import React, { useState, useEffect } from 'react';
import { Card, Button, TextField, Select, Checkbox } from '@shopify/polaris';
import { config } from '../constants';
import { authenticatedFetch } from '../utils/api';

const API_BASE = config.API_BASE;
const daysOfWeek = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'
];

// List of UTC offsets from UTC−12:00 to UTC+14:00 (including half/quarter hours)
const timeZones = [
  'UTC−12:00','UTC−11:00','UTC−10:00','UTC−09:30','UTC−09:00','UTC−08:00','UTC−07:00','UTC−06:00','UTC−05:00','UTC−04:00','UTC−03:30','UTC−03:00','UTC−02:00','UTC−01:00','UTC±00:00','UTC+01:00','UTC+02:00','UTC+03:00','UTC+03:30','UTC+04:00','UTC+04:30','UTC+05:00','UTC+05:30','UTC+05:45','UTC+06:00','UTC+06:30','UTC+07:00','UTC+08:00','UTC+08:45','UTC+09:00','UTC+09:30','UTC+10:00','UTC+10:30','UTC+11:00','UTC+12:00','UTC+12:45','UTC+13:00','UTC+14:00'
];

function getDefaultHours() {
  return daysOfWeek.map(day => ({ day, open: '', close: '', enabled: false, allDay: false }));
}

export default function StoreHoursCard({ storeId }) {
  const [hours, setHours] = useState(getDefaultHours());
  const [timeZone, setTimeZone] = useState('UTC±00:00');
  const [restrictLiveChat, setRestrictLiveChat] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (!storeId) return;
    setLoading(true);
    authenticatedFetch(`${API_BASE}/store-hours?shop=${storeId}`)
      .then(res => res.json())
      .then(data => {
        let loadedHours = Array.isArray(data?.hours) && data.hours.length > 0 ? data.hours : getDefaultHours();
        // Backwards compatibility: add allDay=false if missing
        loadedHours = loadedHours.map(h => ({ ...h, allDay: h.allDay || false }));
        setHours(loadedHours);
        setTimeZone(data?.timeZone || 'UTC±00:00');
        setRestrictLiveChat(!!data?.restrictLiveChat);
        setLoading(false);
      })
      .catch(() => {
        setHours(getDefaultHours());
        setTimeZone('UTC±00:00');
        setRestrictLiveChat(false);
        setLoading(false);
      });
  }, [storeId]);

  const handleTimeChange = (idx, field, val) => {
    const updated = hours.map((h, i) => i === idx ? { ...h, [field]: val } : h);
    setHours(updated);
  };

  const handleEnabledChange = (idx, enabled) => {
    const updated = hours.map((h, i) => i === idx ? { ...h, enabled } : h);
    setHours(updated);
  };

  const handleAllDayChange = (idx, allDay) => {
    const updated = hours.map((h, i) => i === idx ? { ...h, allDay } : h);
    setHours(updated);
  };

  const handleSave = async () => {
    setSaveSuccess(false);
    setSaveError(false);
    if (!storeId) {
      setSaveError(true);
      return;
    }
    try {
      const res = await authenticatedFetch(`${API_BASE}/store-hours?shop=${storeId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ hours, timeZone, restrictLiveChat })
      });
      if (res.ok) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 2500);
      } else {
        setSaveError(true);
        setTimeout(() => setSaveError(false), 2500);
      }
    } catch {
      setSaveError(true);
      setTimeout(() => setSaveError(false), 2500);
    }
  };

  return (
    <Card title="Store Hours" sectioned>
      {saveSuccess && (
        <div style={{ color: '#0a0', background: '#e6ffe6', borderRadius: 8, padding: '8px 16px', margin: '18px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span role="img" aria-label="success">✅</span> Store hours saved!
        </div>
      )}
      {saveError && (
        <div style={{ color: '#b00', background: '#ffeaea', borderRadius: 8, padding: '8px 16px', margin: '18px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span role="img" aria-label="error">⚠️</span> Failed to save store hours.
        </div>
      )}
      {loading ? (
        <div style={{ color: '#888', padding: 12 }}>Loading store hours...</div>
      ) : (
        <>
          <div style={{ marginBottom: 20 }}>
            <Select
              label="Store Time Zone (UTC Offset)"
              options={timeZones.map(tz => ({ label: tz, value: tz }))}
              value={timeZone}
              onChange={setTimeZone}
              helpText="All store hours are interpreted in this UTC offset."
            />
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 500 }}>
              <Checkbox
                label="Enable live chat only during store hours"
                checked={restrictLiveChat}
                onChange={checked => setRestrictLiveChat(checked)}
                id="restrict-live-chat-checkbox"
                style={{ marginBottom: 0 }}
              />
            </div>
            <div style={{ color: '#888', fontSize: 13, marginLeft: 28 }}>
              If unchecked, live chat will be available at all times.
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
            {hours.map((h, idx) => (
              <div key={h.day} style={{
                display: 'flex', alignItems: 'center', gap: 16, background: '#fff', borderRadius: 12,
                padding: '12px 16px', color: '#232323', boxShadow: '0 2px 10px rgba(0,0,0,0.08)'
              }}>
                <label style={{ minWidth: 90, fontWeight: 600 }}>{h.day}</label>
                <Checkbox
                label="Open"
                checked={h.enabled}
                onChange={checked => handleEnabledChange(idx, checked)}
                id={`open-checkbox-${h.day}`}
                style={{ marginBottom: 0, marginRight: 10 }}
              />
                <Checkbox
                label="All day"
                checked={h.allDay}
                disabled={!h.enabled}
                onChange={checked => handleAllDayChange(idx, checked)}
  id={`allday-checkbox-${h.day}`}
  style={{ marginBottom: 0, marginLeft: 10 }}
/>
                <TextField
                  label="Open"
                  labelHidden
                  type="time"
                  value={h.open}
                  onChange={val => handleTimeChange(idx, 'open', val)}
                  disabled={!h.enabled || h.allDay}
                  autoComplete="off"
                  style={{ width: 100 }}
                />
                <span style={{ color: '#888', fontSize: 14 }}>to</span>
                <TextField
                  label="Close"
                  labelHidden
                  type="time"
                  value={h.close}
                  onChange={val => handleTimeChange(idx, 'close', val)}
                  disabled={!h.enabled || h.allDay}
                  autoComplete="off"
                  style={{ width: 100 }}
                />
              </div>
            ))}
          </div>
          <div style={{ height: 28 }} />
          <Button variant="primary" onClick={handleSave} style={{ marginTop: 20 }}>
            Save Store Hours
          </Button>
        </>
      )}
    </Card>
  );
}
